import { memo, useEffect, useRef, useState, useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { C } from "@/lib/colors";
import { api } from "@/lib/api";
import { useTokenDrawer } from "@/stores/token-drawer";
import { useSpreadsStore } from "@/stores/spreads";
import { selectUser, useAuth } from "@/stores/auth";
import type {
  TokenLookupResponse,
  TokenLookupResult,
  TokenContractInfo,
  TokenExchange,
  TokenNetwork,
  TokenTaxInfo,
  TokenTicker,
} from "@/types/token-lookup";
import type { CexMute, MuteScope, MutesResponse } from "@/types/mute";
import { MUTE_SCOPES } from "@/types/mute";
import { fmtPrice, fmtPct, fmtProfit, fmtVol, fmtAgeMinutes } from "@/lib/format";
import { NetBadge } from "@/components/NetBadge";
import { StatusChip } from "@/components/StatusChip";
import { ExchangeIcon } from "@/components/ExchangeIcon";

/** Default scope state for new TokenCards. Both ON: a single click
 *  silences the token across BOTH pipelines (spread alerts AND
 *  dex-dump alerts). Backend's ``_DEFAULT_SCOPE`` is ``["spread"]`` only;
 *  the drawer intentionally widens that default — operators rarely want
 *  to mute one pipeline while leaving the other live, and the chip-level
 *  toggle below stays available for the surgical case. */
type ScopeState = { spread: boolean; dex_dump: boolean };
const DEFAULT_SCOPE_STATE: ScopeState = { spread: true, dex_dump: true };

/** Slide-in token-info drawer.
 *
 *  Mounted ONCE in ``Layout`` so any page can call
 *  ``useTokenDrawer.getState().openFor("$ETH")`` and get a 480 px
 *  panel sliding in from the right with full token details:
 *  exchanges (CEX, with deposit/withdrawal status + per-network mute),
 *  contracts (DEX, with price/liquidity/tax + per-contract mute),
 *  external links (DexScreener / DexTools / GMGN).
 *
 *  When ``spreadUid`` is set (drawer was opened from a Spreads row),
 *  a "Spread details" block is rendered above token info — buy/sell
 *  legs, prices, profit, age, networks, funding. Mirrors the layout of
 *  the legacy ``spreads.html`` ``openPanel + fetchEntityInfo`` so the
 *  user gets one popup with both perspectives. */
export const TokenInfoDrawer = memo(function TokenInfoDrawer() {
  const open = useTokenDrawer((s) => s.open);
  const query = useTokenDrawer((s) => s.query);
  const setQuery = useTokenDrawer((s) => s.setQuery);
  const close = useTokenDrawer((s) => s.close);
  const spreadUid = useTokenDrawer((s) => s.spreadUid);

  // ESC closes — listener attached only while open so background pages
  // don't capture stray Esc presses.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  // Auto-focus the in-drawer search field on open so the user can type
  // a different token without grabbing the mouse. Re-focus on open
  // toggle so ``openFor("$ETH")`` after a previous close still focuses.
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const { data, isFetching, isError, error } = useQuery<TokenLookupResponse>({
    queryKey: ["token-lookup", query],
    queryFn: () => api.get<TokenLookupResponse>(
      `/api/token-lookup?q=${encodeURIComponent(query)}`,
    ),
    enabled: open && query.trim().length >= 2,
    staleTime: 5_000,
  });

  if (!open) return null;

  const results = data?.results ?? [];

  return (
    <>
      {/* Backdrop — click to close. Subtle dim, no heavy blur (terminal
          aesthetic, not modal-fullscreen). */}
      <div
        onClick={close}
        aria-hidden="true"
        className="fixed inset-0 z-40"
        style={{
          background: "rgba(0,0,0,0.45)",
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
        }}
      />

      {/* Drawer */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Token info"
        className="fixed inset-y-0 right-0 z-50 flex flex-col"
        style={{
          width: 480,
          background: C.surface,
          borderLeft: `1px solid ${C.borderHi}`,
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between"
          style={{
            padding: "14px 16px",
            borderBottom: `1px solid ${C.border}`,
            background: C.raised,
          }}
        >
          <div
            className="uppercase font-mono-num"
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.12em",
              color: C.text2,
            }}
          >
            Token Info
          </div>
          <button
            type="button"
            onClick={close}
            className="cursor-pointer"
            style={{
              padding: "4px 10px",
              background: "transparent",
              border: "none",
              color: C.text3,
              fontSize: 13,
              fontWeight: 500,
            }}
            aria-label="Close (Esc)"
            title="Close (Esc)"
          >
            ✕
          </button>
        </div>

        {/* In-drawer search */}
        <div
          style={{
            padding: "10px 12px",
            borderBottom: `1px solid ${C.border}`,
            background: C.surface,
          }}
        >
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Symbol or contract address…"
            spellCheck={false}
            autoComplete="off"
            className="w-full font-mono-num"
            style={{
              padding: "6px 10px",
              fontSize: 12,
              background: C.bg,
              border: `1px solid ${C.border}`,
              color: C.text1,
              outline: "none",
            }}
          />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto" style={{ padding: 12 }}>
          {spreadUid && <SpreadDetailsBlock uid={spreadUid} />}

          {query.trim().length < 2 ? (
            <Hint>Type at least 2 chars to search.</Hint>
          ) : isFetching && !data ? (
            <Hint>Loading…</Hint>
          ) : isError ? (
            <Hint tone="error">
              {(error as Error)?.message || "Lookup failed"}
            </Hint>
          ) : results.length === 0 ? (
            <Hint>Nothing found for {query}.</Hint>
          ) : (
            <div className="flex flex-col" style={{ gap: 12 }}>
              {results.map((t, i) => (
                <TokenCard key={`${t.asset_key}-${i}`} t={t} />
              ))}
            </div>
          )}
        </div>
      </aside>
    </>
  );
});

// ---------------------------------------------------------------------
// Spread-specific block — only rendered when drawer was opened from
// a Spreads row.
// ---------------------------------------------------------------------

const SpreadDetailsBlock = memo(function SpreadDetailsBlock({ uid }: { uid: string }) {
  const row = useSpreadsStore((s) => s.rows.get(uid));
  if (!row) return null;

  const now = Date.now() / 1000;
  const ageMin = row.first_seen_at ? Math.floor((now - row.first_seen_at) / 60) : 0;

  return (
    <div
      style={{
        padding: 12,
        marginBottom: 12,
        background: C.bg,
        border: `1px solid ${C.border}`,
        display: "grid",
        gap: 10,
      }}
    >
      <div className="flex items-baseline" style={{ gap: 8 }}>
        <span
          className="font-mono-num"
          style={{ fontSize: 15, fontWeight: 700, color: C.text1, letterSpacing: "0.4px" }}
        >
          {row.symbol}
        </span>
        <span
          className="font-mono-num uppercase"
          style={{ fontSize: 9, color: C.text3, letterSpacing: "0.08em" }}
        >
          {row.buy_exchange} → {row.sell_exchange}
        </span>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          rowGap: 6,
          columnGap: 12,
        }}
      >
        <Stat label="Spread" value={fmtPct(row.net_spread_pct)} accent={C.mint} />
        <Stat label="Profit" value={fmtProfit(row.net_profit_usd)} accent={C.mintDim} />
        <Stat label="Volume" value={`$${fmtVol(row.volume_usd)}`} />
        <Stat label="Age" value={fmtAgeMinutes(ageMin)} />
      </div>

      <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <SpreadLegBlock
          label="Buy"
          exchange={row.buy_exchange}
          market={row.buy_market}
          ticker={row.buy_ticker ?? null}
          price={row.buy_price}
          fee={row.buy_fee_pct ?? null}
          volumeUsd={row.volume_usd}
          url={row.buy_url ?? null}
        />
        <SpreadLegBlock
          label="Sell"
          exchange={row.sell_exchange}
          market={row.sell_market}
          ticker={row.sell_ticker ?? null}
          price={row.sell_price}
          fee={row.sell_fee_pct ?? null}
          volumeUsd={row.volume_usd}
          url={row.sell_url ?? null}
        />
      </div>

      {row.common_networks && row.common_networks.length > 0 && (
        <div className="grid" style={{ gap: 6 }}>
          <Subhead>Networks</Subhead>
          <div className="flex flex-col" style={{ gap: 4 }}>
            {row.common_networks.map((n, i) => (
              <div key={i} className="flex items-center" style={{ gap: 8 }}>
                <NetBadge net={n.n} />
                <StatusChip ok={!!(n.buy_dep ?? n.dep)} label="dep" />
                <StatusChip ok={!!(n.sell_wd ?? n.wd)} label="wd" />
                {n.fee != null && (
                  <span className="font-mono-num" style={{ fontSize: 10, color: C.text3 }}>
                    fee ${n.fee}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {(row.funding_buy != null || row.funding_sell != null) && (
        <div className="grid" style={{ gap: 4 }}>
          <Subhead>Funding</Subhead>
          {row.funding_buy != null && <KV k="Buy" v={fmtPct(row.funding_buy * 100, 4)} />}
          {row.funding_sell != null && <KV k="Sell" v={fmtPct(row.funding_sell * 100, 4)} />}
        </div>
      )}
    </div>
  );
});

function SpreadLegBlock({
  label,
  exchange,
  market,
  ticker,
  price,
  fee,
  volumeUsd,
  url,
}: {
  label: string;
  exchange: string;
  market: string;
  ticker: string | null;
  price: number;
  fee: number | null;
  volumeUsd: number;
  url: string | null;
}) {
  // Solana / TON tickers are full base58 contract addresses (44-48
  // chars) — at 10 px mono the rendered string is ~250-300 px,
  // overflowing each ``1fr`` column of the Buy/Sell grid. Abbreviate
  // for the visible cell; the full address is in ``title``-tooltip
  // via the upgraded KV truncation. CEX-shaped tickers pass through
  // unchanged.
  const tickShown = ticker ? abbrevAddr(ticker, 8, 6) : "—";
  // Exchange name doubles as the trade-pair link when ``url`` is
  // present — clicking it opens the deep-link to that exchange's
  // book for this asset/market. Replaces the previous separate
  // "Open ↗" link below for a tighter card. Capitalised so it reads
  // as a brand name (``Asterdex`` not ``asterdex``); ``(market)`` tag
  // stays mono lowercase to keep the visual distinction.
  const exchangeNode =
    url ? (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        title={`Open ${exchange} ${market} chart`}
        style={{
          color: C.mintDim,
          textDecoration: "none",
        }}
      >
        <ExchangeIcon exchange={exchange} size={14} className="mr-1 align-middle" />
        {capitalize(exchange)}
        <span style={{ color: C.text4 }}> ({market})</span>
      </a>
    ) : (
      <span>
        <ExchangeIcon exchange={exchange} size={14} className="mr-1 align-middle" />
        {capitalize(exchange)}
        <span style={{ color: C.text4 }}> ({market})</span>
      </span>
    );
  return (
    <div style={{ display: "grid", gap: 4, minWidth: 0 }}>
      <Subhead>{label}</Subhead>
      <KVNode k="Exch" node={exchangeNode} />
      <KV k="Tick" v={tickShown} fullV={ticker ?? undefined} />
      <KV k="Price" v={fmtPrice(price)} />
      {fee != null && (
        // Trading fee as a dollar amount at the suggested order size
        // (``volume_usd`` from the spread row) — more actionable than
        // the raw % when the user is sizing a real trade. Title
        // tooltip preserves the original percentage for reference
        // (so the user can sanity-check the rate the exchange
        // publishes against what we're using).
        <KV
          k="Fee"
          v={fmtFeeUsd(fee, volumeUsd)}
          fullV={`${fmtPct(fee, 3)} of $${Math.round(volumeUsd)}`}
        />
      )}
    </div>
  );
}

/** Compute and format the trading fee as a USD amount given a
 *  percentage rate and a notional order size. ``fee_pct`` arrives in
 *  percentage units (``0.1`` = 0.1 %), matching the analyzer's
 *  ``buy_fee_pct``/``sell_fee_pct`` shape. */
function fmtFeeUsd(feePct: number, volumeUsd: number): string {
  const usd = (feePct / 100) * volumeUsd;
  if (!isFinite(usd)) return "—";
  if (usd === 0) return "$0";
  if (usd < 0.01) return "<$0.01";
  if (usd < 1) return `$${usd.toFixed(2)}`;
  if (usd < 100) return `$${usd.toFixed(2)}`;
  return `$${Math.round(usd)}`;
}

/** Same layout as ``KV`` but the value cell can be any ``ReactNode``
 *  (typically a link). Shares the truncation/min-width semantics so
 *  long content still folds gracefully into the column. */
function KVNode({ k, node }: { k: string; node: React.ReactNode }) {
  return (
    <div className="flex items-baseline" style={{ gap: 6, minWidth: 0 }}>
      <span style={{ fontSize: 9, color: C.text4, flexShrink: 0 }}>{k}</span>
      <span
        className="font-mono-num"
        style={{
          fontSize: 10,
          color: C.text2,
          marginLeft: "auto",
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textAlign: "right",
        }}
      >
        {node}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------
// Token card — exchanges + contracts + ext links.
// ---------------------------------------------------------------------

const TokenCard = memo(function TokenCard({ t }: { t: TokenLookupResult }) {
  const sym = (t.symbol || "").toLowerCase();
  // Mute controls are admin-only — backend ``Depends(require_admin)``
  // returns 401/403 for everyone else, and the buttons would silently
  // do nothing (mutations have no onError handler). Hide them here so
  // the UI doesn't lie about what's actionable. Backend remains the
  // source of truth (defense-in-depth).
  const user = useAuth(selectUser);
  const isAdmin = user?.role === "admin";

  // Scope state for every mute click in this card (asset + per-exchange
  // + per-contract). Persists across re-renders, resets only when the
  // drawer remounts the card (different search query). See
  // ``DEFAULT_SCOPE_STATE`` for rationale on the default.
  const [scope, setScope] = useState<ScopeState>(DEFAULT_SCOPE_STATE);
  const scopeArr: MuteScope[] = MUTE_SCOPES.filter((s) => scope[s]);

  // Active mute lookups — rendered as badges next to the asset / exchange
  // / contract row that the mute targets. Same query key as the Mutes
  // page so a click fired anywhere in the app triggers a single shared
  // refetch (mutations invalidate ``["mutes"]``). 5 s staleness keeps the
  // badge fresh enough to confirm a click without spamming the endpoint.
  const { data: mutesData } = useQuery<MutesResponse>({
    queryKey: ["mutes"],
    queryFn: () => api.get<MutesResponse>("/api/mutes"),
    staleTime: 5_000,
    enabled: isAdmin,  // anon/non-admin can't mute → no need to fetch
  });
  const mutes: CexMute[] = mutesData?.mutes ?? [];
  const assetMute = sym ? findAssetMute(mutes, sym) : undefined;

  return (
    <div
      style={{
        padding: 12,
        background: C.bg,
        border: `1px solid ${C.border}`,
        display: "grid",
        gap: 10,
      }}
    >
      <div className="flex items-baseline" style={{ gap: 8, flexWrap: "wrap" }}>
        <span
          className="font-mono-num"
          style={{ fontSize: 15, fontWeight: 700, color: C.text1, letterSpacing: "0.3px" }}
        >
          {t.symbol}
        </span>
        {t.name && t.name.toUpperCase() !== t.symbol && (
          <span style={{ fontSize: 11, color: C.text3 }}>{t.name}</span>
        )}
        {assetMute && <MuteBadge mute={assetMute} />}
      </div>

      <ExternalLinks t={t} />

      {/* Scope selector — applies to every mute button in this card.
          Single source of truth so the operator picks scope once and
          all subsequent durations / levels (asset / exchange / contract)
          inherit it. Hidden for non-admins (no mute UI surface). */}
      {isAdmin && (
        <ScopeSelector value={scope} onChange={setScope} />
      )}

      {/* Asset-level mute (admin-only). */}
      {isAdmin && sym && (
        <div className="flex items-center" style={{ gap: 6, flexWrap: "wrap" }}>
          <span style={{ fontSize: 10, color: C.text4 }}>🔇 asset</span>
          <MuteBtn variant="red" body={{ type: "asset", key: sym, scope: scopeArr }} disabled={scopeArr.length === 0}>perm</MuteBtn>
          <MuteBtn variant="amber" body={{ type: "asset", key: sym, hours: 24, scope: scopeArr }} disabled={scopeArr.length === 0}>24h</MuteBtn>
          <MuteBtn variant="amber" body={{ type: "asset", key: sym, hours: 72, scope: scopeArr }} disabled={scopeArr.length === 0}>72h</MuteBtn>
          <MuteBtn variant="amber" body={{ type: "asset", key: sym, hours: 720, scope: scopeArr }} disabled={scopeArr.length === 0}>30d</MuteBtn>
          <UnmuteBtn id={sym}>unmute</UnmuteBtn>
        </div>
      )}

      <Subhead>Exchanges</Subhead>
      <div className="flex flex-col" style={{ gap: 8 }}>
        {t.exchanges.map((ex) => (
          <ExchangeRow
            key={ex.exchange}
            ex={ex}
            prices={t.prices}
            sym={sym}
            isAdmin={isAdmin}
            scopeArr={scopeArr}
            mute={findExchangeMute(mutes, sym, ex.exchange)}
            contracts={t.contracts}
          />
        ))}
      </div>

      {Object.keys(t.contracts).length > 0 && (
        <>
          <Subhead>Contracts (DEX)</Subhead>
          <div className="flex flex-col" style={{ gap: 8 }}>
            {Object.entries(t.contracts).map(([net, c]) => (
              <ContractRow
                key={net}
                net={net}
                c={c}
                price={t.dex_prices?.[net] ?? null}
                liq={t.dex_liq?.[net] ?? null}
                tax={t.taxes?.[net] ?? null}
                isAdmin={isAdmin}
                scopeArr={scopeArr}
                mute={findContractMute(mutes, c.chain, c.address)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
});

// ---------------------------------------------------------------------
// Scope selector — two toggle chips wired to the parent's mute clicks.
// Mirrors ``Mutes/index.tsx`` colour mapping (mint = spread, amber =
// dex-dump) so the same semantic palette is recognisable across pages.
// ---------------------------------------------------------------------

function ScopeSelector({
  value,
  onChange,
}: {
  value: ScopeState;
  onChange: (next: ScopeState) => void;
}) {
  return (
    <div className="flex items-center" style={{ gap: 6, flexWrap: "wrap" }}>
      <span
        className="uppercase font-mono-num"
        style={{ fontSize: 9, color: C.text4, letterSpacing: "0.08em" }}
      >
        Scope
      </span>
      <ScopeChip
        label="spread"
        on={value.spread}
        accent={C.mint}
        onClick={() => onChange({ ...value, spread: !value.spread })}
      />
      <ScopeChip
        label="dex-dump"
        on={value.dex_dump}
        accent={C.amber}
        onClick={() => onChange({ ...value, dex_dump: !value.dex_dump })}
      />
      {!value.spread && !value.dex_dump && (
        <span
          className="uppercase font-mono-num"
          style={{ fontSize: 9, color: C.red, letterSpacing: "0.08em" }}
        >
          pick one
        </span>
      )}
    </div>
  );
}

function ScopeChip({
  label,
  on,
  onClick,
  accent,
}: {
  label: string;
  on: boolean;
  onClick: () => void;
  accent: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="font-mono-num uppercase cursor-pointer"
      style={{
        padding: "1px 6px",
        fontSize: 9,
        fontWeight: 600,
        letterSpacing: "0.06em",
        color: on ? accent : C.text4,
        background: on ? `${accent}1a` : "transparent",
        border: `1px solid ${on ? `${accent}3a` : C.border}`,
      }}
    >
      {on ? "✓ " : ""}
      {label}
    </button>
  );
}

function ExternalLinks({ t }: { t: TokenLookupResult }) {
  // External providers: CMC + CoinGecko (from mapping_human.json) +
  // DexScreener / DexTools / GMGN (derived from first DEX contract).
  const links: { label: string; href: string }[] = [];
  if (t.cmc_url) links.push({ label: "CMC", href: t.cmc_url });
  if (t.coingecko_url) links.push({ label: "CoinGecko", href: t.coingecko_url });

  const firstNet = Object.keys(t.contracts)[0];
  const firstC = firstNet ? t.contracts[firstNet] : null;
  if (firstC) {
    const dsChain: Record<string, string> = {
      eth: "ethereum",
      bsc: "bsc",
      sol: "solana",
      arb: "arbitrum",
      base: "base",
      polygon: "polygon",
      op: "optimism",
      avax: "avalanche",
    };
    const dsKey = dsChain[firstC.chain] ?? firstC.chain;
    links.push({
      label: "DexScreener",
      href: `https://dexscreener.com/${dsKey}/${firstC.address}`,
    });
    links.push({
      label: "DexTools",
      href: `https://www.dextools.io/app/en/${dsKey}/pair-explorer/${firstC.address}`,
    });
    if (firstC.chain === "sol") {
      links.push({
        label: "GMGN",
        href: `https://gmgn.ai/sol/token/${firstC.address}`,
      });
    }
  }

  if (links.length === 0) return null;

  return (
    <div className="flex flex-wrap" style={{ gap: 6 }}>
      {links.map((l) => (
        <a
          key={l.href}
          href={l.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="no-underline"
          style={{
            padding: "2px 8px",
            fontSize: 10,
            background: C.raised,
            border: `1px solid ${C.border}`,
            color: C.text2,
          }}
        >
          {l.label} ↗
        </a>
      ))}
    </div>
  );
}

const ExchangeRow = memo(function ExchangeRow({
  ex,
  prices,
  sym,
  isAdmin,
  scopeArr,
  mute,
  contracts,
}: {
  ex: TokenExchange;
  prices: Record<string, number> | undefined;
  sym: string;
  isAdmin: boolean;
  scopeArr: MuteScope[];
  mute?: CexMute;
  /** Canonical contracts map (``TokenLookupResult.contracts``) — passed
   *  through so per-network ``NetworkLine`` can flag a mismatch when
   *  THIS exchange's ``contract_address`` differs from the canonical
   *  ``dexs[NET]`` value. ``undefined`` is fine — skips the highlight. */
  contracts?: Record<string, TokenContractInfo>;
}) {
  const sp = prices?.[`${ex.exchange}:spot`];
  const fp = prices?.[`${ex.exchange}:futures`];
  const refPrice = sp ?? fp;
  const spotTk = ex.tickers.find((tk) => tk.market === "spot");
  const futTk = ex.tickers.find((tk) => tk.market === "futures");
  const noScope = scopeArr.length === 0;

  return (
    <div className="grid" style={{ gap: 4 }}>
      {/*
        Per-row 3-column grid so exchange names + prices align
        vertically across the whole list. Without this each row is
        an independent flex container — names of varying widths
        ("Mexc" vs "Hyperliquid") push the ``s:/f:`` price labels
        to different X positions, breaking scan-ability when a user
        compares prices across exchanges. ``96px`` accommodates the
        longest name we ship (``Hyperliquid``, 11 chars at 12 px ≈
        86 px + breathing room); shorter names left-align inside
        the cell.

        Column 2 (``min-content``) sizes to whichever combination
        of badge + s/f price links the row actually has — naturally
        compact for futures-only rows like the screenshot. Column 3
        (``auto`` for admin, omitted otherwise) hosts the mute
        buttons floated to the right edge.
      */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isAdmin ? "96px 1fr auto" : "96px 1fr",
          alignItems: "baseline",
          columnGap: 8,
        }}
      >
        <span
          className="font-mono-num"
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: C.text1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={ex.exchange}
        >
          <ExchangeIcon exchange={ex.exchange} size={14} className="mr-1 align-[-2px]" />
          {capitalize(ex.exchange)}
        </span>
        <span
          className="flex items-baseline"
          style={{ gap: 6, minWidth: 0 }}
        >
          {mute && <MuteBadge mute={mute} />}
          {/*
            Two-slot grid for prices: spot on the left, futures on the
            right, EVERY row. When only one is present the other slot
            stays empty (not collapsed) so prices align vertically
            across the full exchange list — without this, futures-only
            rows like ``Bitmart`` rendered ``f:`` in the spot column
            and broke scan-ability when comparing prices column-down.
          */}
          <span
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              alignItems: "baseline",
              columnGap: 8,
              flex: 1,
              minWidth: 0,
            }}
          >
            <span style={{ minWidth: 0 }}>
              {sp != null && (
                <PriceLink label="S" price={sp} tk={spotTk ?? null} />
              )}
            </span>
            <span style={{ minWidth: 0 }}>
              {fp != null && (
                <PriceLink label="F" price={fp} tk={futTk ?? null} />
              )}
            </span>
          </span>
        </span>
        {isAdmin && (
          <span className="flex items-center" style={{ gap: 4 }}>
            <MuteBtnSmall
              variant="red"
              body={{ type: "exchange", key: sym, exchange: ex.exchange, scope: scopeArr }}
              title="Mute exchange permanently"
              disabled={noScope}
            >🔇</MuteBtnSmall>
            <MuteBtnSmall
              variant="amber"
              body={{ type: "exchange", key: sym, exchange: ex.exchange, hours: 24, scope: scopeArr }}
              disabled={noScope}
            >24h</MuteBtnSmall>
            <MuteBtnSmall
              variant="amber"
              body={{ type: "exchange", key: sym, exchange: ex.exchange, hours: 72, scope: scopeArr }}
              disabled={noScope}
            >72h</MuteBtnSmall>
            <UnmuteBtnSmall id={`${sym}@${ex.exchange}`}>✓</UnmuteBtnSmall>
          </span>
        )}
      </div>
      {ex.networks.length > 0 && (
        <div className="flex flex-col" style={{ gap: 1, paddingLeft: 8 }}>
          {ex.networks.map((n) => {
            const canonical =
              (contracts || {})[n.network]?.address || null;
            return (
              <NetworkLine
                key={n.network}
                n={n}
                refPrice={refPrice ?? null}
                exchange={ex.exchange}
                canonical={canonical}
              />
            );
          })}
        </div>
      )}
    </div>
  );
});

function PriceLink({
  label,
  price,
  tk,
}: {
  label: string;
  price: number;
  tk: TokenTicker | null;
}) {
  const formatted = fmtPrice(price);
  return (
    <span style={{ fontSize: 11 }}>
      <span style={{ color: C.text4 }}>{label}:</span>{" "}
      {tk?.trade_url ? (
        <a
          href={tk.trade_url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="no-underline font-mono-num"
          style={{ color: C.mintDim }}
        >
          {formatted}
        </a>
      ) : (
        <span className="font-mono-num" style={{ color: C.text2 }}>{formatted}</span>
      )}
    </span>
  );
}

function NetworkLine({
  n,
  refPrice,
  exchange,
  canonical,
}: {
  n: TokenNetwork;
  refPrice: number | null;
  /** Owning exchange — used in the contract-chip tooltip ("MISMATCH —
   *  {exchange} reports …"). */
  exchange: string;
  /** Canonical contract for this network from
   *  ``TokenLookupResult.contracts[net].address``. ``null`` when the
   *  entity has no canonical contract at all for this chain (chip
   *  still renders, just no mismatch comparison). */
  canonical: string | null;
}) {
  const dEm = n.deposit === true ? "🟢" : n.deposit === false ? "🔴" : "❔";
  const wEm = n.withdrawal === true ? "🟢" : n.withdrawal === false ? "🔴" : "❔";
  const dAge = timeAgoShort(n.deposit_changed);
  const wAge = timeAgoShort(n.withdrawal_changed);
  let feeStr = "—";
  if (n.withdrawal_fee != null && refPrice && refPrice > 0) {
    const usd = n.withdrawal_fee * refPrice;
    feeStr =
      usd >= 1 ? `$${usd.toFixed(0)}` :
      usd >= 0.01 ? `$${usd.toFixed(2)}` :
      `$${usd.toFixed(4)}`;
  } else if (n.withdrawal_fee === 0) {
    feeStr = "$0";
  }
  // Per-exchange contract claim (sourced from
  // ``cexs.<ex>.networks[net].contract_address`` via the API). When
  // present and differs from the canonical pick, the chip switches to
  // amber + "⚠" — same intent as the ``mapping contract mismatch``
  // log line (ASTEROID/LO0P/BOBO class). Click copies the full address.
  const reported = n.contract_address || null;
  return (
    <div
      className="font-mono-num"
      style={{
        fontSize: 10,
        color: C.text3,
        lineHeight: 1.4,
        // Fixed-px columns so network / deposit / withdrawal / fee / contract
        // line up vertically across EVERY row of the exchange. Each network row
        // is its own grid, so the widths must be fixed px (not auto/max-content)
        // to align across rows — this replaces the old padEnd() text-padding,
        // which broke because the 🟢/🔴/❔ glyphs aren't 1 monospace cell wide.
        display: "grid",
        gridTemplateColumns: "90px 44px 44px 50px 1fr",
        columnGap: 8,
        alignItems: "baseline",
      }}
    >
      <span
        style={{ color: C.text2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
        title={n.network}
      >
        {n.network}
      </span>
      {/* Deposit + withdrawal: the status glyph sits in a fixed-width slot so
          the age text after it aligns down the column regardless of emoji. */}
      <span style={{ whiteSpace: "nowrap" }}>
        <span style={{ display: "inline-block", width: 16 }}>{dEm}</span>{dAge}
      </span>
      <span style={{ whiteSpace: "nowrap" }}>
        <span style={{ display: "inline-block", width: 16 }}>{wEm}</span>{wAge}
      </span>
      <span style={{ color: C.text2, whiteSpace: "nowrap" }}>{feeStr}</span>
      <span style={{ minWidth: 0, overflow: "hidden" }}>
        {reported && (
          <DrawerContractChip
            reported={reported}
            canonical={canonical}
            exchange={exchange}
          />
        )}
      </span>
    </div>
  );
}

/** Inline per-exchange contract chip rendered in the drawer's
 *  per-network row. Mirrors the standalone-page ``ContractChip``
 *  (TokenInfo/index.tsx) — truncated address, click-to-copy,
 *  amber+⚠ when ``reported`` differs from ``canonical``. */
function DrawerContractChip({
  reported,
  canonical,
  exchange,
}: {
  reported: string;
  canonical: string | null;
  exchange: string;
}) {
  // EVM equality is **case-insensitive** AND **leading-zero-insensitive**
  // — backend's ``_norm_contract`` (human_mapping.py) strips leading
  // zeros after ``0x`` and lowercases, so the per-exchange claim ends up
  // like ``0xe63b9c…25a9`` while the canonical (sourced from CG /
  // wormhole / raw exchange field) often keeps the EIP-55 mixed-case +
  // any leading zero ``0x0E63B9C…25A9``. Naive ``toLowerCase()`` compare
  // would flag this as MISMATCH even though they're the same address.
  // ``addrKey`` collapses both to one canonical form for comparison.
  const mismatch = !!canonical && addrKey(reported) !== addrKey(canonical);
  const [copied, setCopied] = useState(false);
  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      void navigator.clipboard?.writeText(reported);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    },
    [reported],
  );
  const color = copied ? C.mint : mismatch ? C.amber : C.text4;
  const title = mismatch
    ? `MISMATCH — ${exchange} reports ${reported}\ncanonical: ${canonical}\n(click to copy reported address)`
    : `${exchange} reports this contract on this chain — click to copy\n${reported}`;
  return (
    <span
      className="cursor-pointer transition-colors hover:text-text2"
      onClick={onClick}
      title={title}
      style={{
        color,
        fontWeight: mismatch ? 600 : 400,
        whiteSpace: "nowrap",
      }}
    >
      {mismatch && (
        <span aria-hidden="true" style={{ marginRight: 2 }}>
          ⚠
        </span>
      )}
      {copied ? "✓ copied" : shortAddr(reported)}
    </span>
  );
}

/** Truncate ``0xabc…def0`` / base58 / similar long addresses to
 *  ``prefix…suffix`` for inline UI. Short or non-address values pass
 *  through unchanged. */
function shortAddr(s: string): string {
  if (!s) return "";
  if (s.length <= 14) return s;
  // Charset-agnostic middle-ellipsis — covers TON base64url ("-"/"_") which
  // the old [A-Za-z0-9] regex left full-length and overflowing.
  return `${s.slice(0, 6)}…${s.slice(-4)}`;
}

/** Collapse an address into a canonical comparison key matching the
 *  backend's ``_norm_contract`` (``src/human_mapping.py``):
 *
 *  * EVM (``0x``-prefixed): lowercase + strip leading zeros after the
 *    ``0x`` prefix. Without the leading-zero strip, ``0xe63b9c…25a9``
 *    (per-exchange claim, normalised by backend) and
 *    ``0x0E63B9C…25A9`` (canonical pick that kept EIP-55 casing and a
 *    leading zero) would compare as MISMATCH even though they're the
 *    same 20-byte address. The ``replace(/^0+/, "")`` falls back to
 *    ``"0"`` for the all-zero address.
 *  * Non-EVM (Solana / Tron / TON / etc.) — case-sensitive on the
 *    chain itself, so we ONLY trim whitespace. A SOL address with
 *    different casing IS a different address. */
function addrKey(s: string): string {
  if (!s) return "";
  const t = s.trim();
  if (t.toLowerCase().startsWith("0x")) {
    const rest = t.slice(2).toLowerCase().replace(/^0+/, "");
    return "0x" + (rest || "0");
  }
  return t;
}

const ContractRow = memo(function ContractRow({
  net,
  c,
  price,
  liq,
  tax,
  isAdmin,
  scopeArr,
  mute,
}: {
  net: string;
  c: TokenContractInfo;
  price: number | null;
  liq: number | null;
  tax: TokenTaxInfo | null;
  isAdmin: boolean;
  scopeArr: MuteScope[];
  mute?: CexMute;
}) {
  const noScope = scopeArr.length === 0;
  return (
    <div className="grid" style={{ gap: 2 }}>
      <div className="flex items-baseline" style={{ gap: 6, flexWrap: "wrap" }}>
        <span
          className="font-mono-num"
          style={{ fontSize: 11, fontWeight: 700, color: C.text1 }}
        >
          {net}
        </span>
        {mute && <MuteBadge mute={mute} />}
        {c.source === "coingecko" && (
          <Badge color={C.amber} label="CG" title="Source: CoinGecko" />
        )}
        {c.source === "wormhole" && (
          <Badge color={C.purple} label="WH" title="Source: Wormhole" />
        )}
        {price != null && (
          <span className="font-mono-num" style={{ fontSize: 11, color: C.text2 }}>
            {fmtPrice(price)}
          </span>
        )}
        {liq != null && (
          <span style={{ fontSize: 10, color: C.text4 }}>
            Liq:{" "}
            <span className="font-mono-num" style={{ color: C.text3 }}>
              {fmtLiq(liq)}
            </span>
          </span>
        )}
        {tax && <TaxBadge tax={tax} />}
        {isAdmin && (
          <span className="ml-auto flex items-center" style={{ gap: 4 }}>
            <MuteBtnSmall
              variant="red"
              body={{ type: "contract", chain: c.chain, contract: c.address, scope: scopeArr }}
              title="Mute contract permanently"
              disabled={noScope}
            >🔇</MuteBtnSmall>
            <MuteBtnSmall
              variant="amber"
              body={{ type: "contract", chain: c.chain, contract: c.address, hours: 24, scope: scopeArr }}
              disabled={noScope}
            >24h</MuteBtnSmall>
            <MuteBtnSmall
              variant="amber"
              body={{ type: "contract", chain: c.chain, contract: c.address, hours: 72, scope: scopeArr }}
              disabled={noScope}
            >72h</MuteBtnSmall>
            <UnmuteBtnSmall id={`${c.chain}:${c.address}`}>✓</UnmuteBtnSmall>
          </span>
        )}
      </div>
      <div
        className="font-mono-num"
        style={{ fontSize: 9, color: C.text3, paddingLeft: 4, wordBreak: "break-all" }}
      >
        {c.address}
      </div>
    </div>
  );
});

function TaxBadge({ tax }: { tax: TokenTaxInfo }) {
  if (tax.is_honeypot) {
    return (
      <Badge
        color={C.red}
        label="🍯 HP"
        title="Honeypot detected"
      />
    );
  }
  if (tax.buy_tax > 0 || tax.sell_tax > 0) {
    const bad = tax.buy_tax > 5 || tax.sell_tax > 5;
    return (
      <Badge
        color={bad ? C.red : C.amber}
        label={`B:${tax.buy_tax}% S:${tax.sell_tax}%`}
        title="Buy / sell tax"
      />
    );
  }
  return (
    <span
      className="font-mono-num"
      style={{ fontSize: 9, color: C.mint }}
      title="No tax detected"
    >
      0%
    </span>
  );
}

// ---------------------------------------------------------------------
// Active-mute lookup + badge.
//
// The drawer fetches ``/api/mutes`` (cached via React Query under the
// shared ``["mutes"]`` key — same as Mutes page) and renders a compact
// ``🔇 SD 24h`` chip next to whichever asset / exchange / contract row
// has a matching mute entry. After clicking a mute button the mutation
// invalidates ``["mutes"]`` → refetch → badge appears, giving the
// operator visual confirmation the click took effect.
//
// Match rules mirror the backend ``MuteManager``:
//   * ``type=asset, key=<sym>``           → applies to the whole TokenCard
//   * ``type=exchange, key=<sym>, exchange=<ex>``  → an ExchangeRow
//   * ``type=contract, chain=<chain>, contract=<addr>``  → a ContractRow
//
// All comparisons are case-insensitive — backend normalises lower, but
// older audit rows may have different casing; defense-in-depth.
// ---------------------------------------------------------------------

function findAssetMute(mutes: CexMute[], sym: string): CexMute | undefined {
  const target = sym.toLowerCase();
  return mutes.find(
    (m) => m.type === "asset" && (m.key || "").toLowerCase() === target,
  );
}

function findExchangeMute(
  mutes: CexMute[],
  sym: string,
  exchange: string,
): CexMute | undefined {
  const targetSym = sym.toLowerCase();
  const targetEx = exchange.toLowerCase();
  return mutes.find(
    (m) =>
      m.type === "exchange" &&
      (m.key || "").toLowerCase() === targetSym &&
      (m.exchange || "").toLowerCase() === targetEx,
  );
}

function findContractMute(
  mutes: CexMute[],
  chain: string,
  contract: string,
): CexMute | undefined {
  const targetChain = chain.toLowerCase();
  const targetCa = contract.toLowerCase();
  return mutes.find(
    (m) =>
      m.type === "contract" &&
      (m.chain || "").toLowerCase() === targetChain &&
      (m.contract || "").toLowerCase() === targetCa,
  );
}

/** Badge rendered next to a row whose target is currently muted. Red
 *  when the mute is permanent, amber otherwise — same semantic palette
 *  as the active mute buttons so the visual rhythm stays consistent.
 *  Scope letters (``S`` = spread, ``D`` = dex_dump) and remaining time
 *  (``perm`` / ``2h`` / ``3d``) keep the badge readable at 9 px. */
function MuteBadge({ mute }: { mute: CexMute }) {
  const scopes: MuteScope[] =
    mute.scope && mute.scope.length > 0 ? mute.scope : ["spread"];
  const scopeLabel = scopes
    .map((s) => (s === "spread" ? "S" : "D"))
    .join("");

  // ``until`` is either an ISO 8601 string, an epoch-seconds number, or
  // ``null`` for permanent mutes — backend's ``MuteEntry.to_dict`` writes
  // any of the three depending on origin. Normalise to seconds-remaining
  // and bucket into a short human label.
  let timeLabel = "perm";
  let isPerm = true;
  if (mute.until != null) {
    isPerm = false;
    let untilSec: number | null = null;
    if (typeof mute.until === "number") {
      untilSec = mute.until;
    } else if (typeof mute.until === "string" && mute.until) {
      const parsed = Date.parse(mute.until);
      if (Number.isFinite(parsed)) untilSec = parsed / 1000;
    }
    if (untilSec != null) {
      const remainingSec = untilSec - Date.now() / 1000;
      if (remainingSec <= 0) {
        timeLabel = "exp";
      } else if (remainingSec < 3600) {
        timeLabel = `${Math.max(1, Math.floor(remainingSec / 60))}m`;
      } else if (remainingSec < 86400) {
        timeLabel = `${Math.floor(remainingSec / 3600)}h`;
      } else {
        timeLabel = `${Math.floor(remainingSec / 86400)}d`;
      }
    }
  }

  const fg = isPerm ? C.red : C.amber;
  const tooltipScopes = scopes.join("+");
  const tooltipReason = mute.reason ? ` · ${mute.reason}` : "";

  return (
    <span
      className="font-mono-num uppercase"
      style={{
        padding: "1px 6px",
        fontSize: 9,
        fontWeight: 600,
        letterSpacing: "0.06em",
        color: fg,
        background: `${fg}1a`,
        border: `1px solid ${fg}3a`,
      }}
      title={`Muted ${tooltipScopes} · ${timeLabel}${tooltipReason}`}
    >
      🔇 {scopeLabel} {timeLabel}
    </span>
  );
}

// ---------------------------------------------------------------------
// Mute / unmute buttons.
// ---------------------------------------------------------------------

interface MuteBody {
  type: "asset" | "contract" | "exchange";
  key?: string;
  chain?: string;
  contract?: string;
  exchange?: string;
  hours?: number;
  /** Pipelines this mute applies to. Backend's ``_normalize_scope``
   *  silently drops unknowns and falls back to ``["spread"]`` when
   *  empty — the drawer disables the click in that case so we never
   *  accidentally fall back to "spread-only" when the operator
   *  intended both. */
  scope?: MuteScope[];
}

function useMuteMutation() {
  const qc = useQueryClient();
  const m = useMutation<unknown, Error, MuteBody>({
    mutationFn: (body) => api.post("/api/mute", { ...body, reason: "drawer" }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["mutes"] });
      qc.invalidateQueries({ queryKey: ["dex-mutes"] });
      qc.invalidateQueries({ queryKey: ["token-lookup"] });
    },
  });
  return m;
}

function useUnmuteMutation() {
  const qc = useQueryClient();
  const m = useMutation<unknown, Error, string>({
    mutationFn: (identifier) => api.post("/api/unmute", { identifier }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["mutes"] });
      qc.invalidateQueries({ queryKey: ["dex-mutes"] });
      qc.invalidateQueries({ queryKey: ["token-lookup"] });
    },
  });
  return m;
}

function MuteBtn({
  body,
  variant,
  children,
  title,
  disabled,
}: {
  body: MuteBody;
  variant: "red" | "amber";
  children: React.ReactNode;
  title?: string;
  disabled?: boolean;
}) {
  const m = useMuteMutation();
  const palette = variant === "red"
    ? { fg: C.red, bg: C.redBg, border: "rgba(255,87,119,0.25)" }
    : { fg: C.amber, bg: C.amberBg, border: "rgba(242,181,83,0.25)" };
  const blocked = disabled || m.isPending;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        if (disabled) return;
        m.mutate(body);
      }}
      disabled={blocked}
      title={disabled ? "Pick at least one scope" : title}
      style={{
        padding: "1px 6px",
        fontSize: 10,
        fontWeight: 600,
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        color: palette.fg,
        cursor: m.isPending ? "wait" : disabled ? "not-allowed" : "pointer",
        opacity: blocked ? 0.4 : 1,
      }}
    >
      {children}
    </button>
  );
}

function MuteBtnSmall(props: {
  body: MuteBody;
  variant: "red" | "amber";
  children: React.ReactNode;
  title?: string;
  disabled?: boolean;
}) {
  const m = useMuteMutation();
  const palette = props.variant === "red"
    ? { fg: C.red, bg: C.redBg }
    : { fg: C.amber, bg: C.amberBg };
  const blocked = props.disabled || m.isPending;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        if (props.disabled) return;
        m.mutate(props.body);
      }}
      disabled={blocked}
      title={props.disabled ? "Pick at least one scope" : props.title}
      style={{
        padding: "0 4px",
        fontSize: 9,
        fontWeight: 600,
        background: palette.bg,
        border: "none",
        color: palette.fg,
        cursor: m.isPending ? "wait" : props.disabled ? "not-allowed" : "pointer",
        opacity: blocked ? 0.4 : 1,
      }}
    >
      {props.children}
    </button>
  );
}

function UnmuteBtn({ id, children }: { id: string; children: React.ReactNode }) {
  const m = useUnmuteMutation();
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); m.mutate(id); }}
      disabled={m.isPending}
      style={{
        padding: "1px 6px",
        fontSize: 10,
        fontWeight: 600,
        background: C.mintBg,
        border: "1px solid rgba(0,215,142,0.25)",
        color: C.mint,
        cursor: m.isPending ? "wait" : "pointer",
        opacity: m.isPending ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  );
}

function UnmuteBtnSmall({ id, children }: { id: string; children: React.ReactNode }) {
  const m = useUnmuteMutation();
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); m.mutate(id); }}
      disabled={m.isPending}
      style={{
        padding: "0 4px",
        fontSize: 9,
        fontWeight: 600,
        background: C.mintBg,
        border: "none",
        color: C.mint,
        cursor: m.isPending ? "wait" : "pointer",
        opacity: m.isPending ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------
// Tiny presentation helpers.
// ---------------------------------------------------------------------

function Subhead({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="uppercase"
      style={{
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: "0.12em",
        color: C.text3,
        borderBottom: `1px solid ${C.border}`,
        paddingBottom: 4,
      }}
    >
      {children}
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: string;
}) {
  return (
    <div className="flex items-baseline" style={{ gap: 6 }}>
      <span
        className="uppercase"
        style={{
          fontSize: 9,
          letterSpacing: "0.08em",
          color: C.text3,
          minWidth: 50,
        }}
      >
        {label}
      </span>
      <span
        className="font-mono-num"
        style={{ fontSize: 13, fontWeight: 700, color: accent ?? C.text1 }}
      >
        {value}
      </span>
    </div>
  );
}

function KV({
  k,
  v,
  fullV,
}: {
  k: string;
  v: string;
  /** Optional full text shown in the title-tooltip when ``v`` itself
   *  is already abbreviated for display. Defaults to ``v`` (short
   *  tickers like ``BTCUSDT`` need no separate tooltip). */
  fullV?: string;
}) {
  // ``min-width: 0`` on the value cell lets the flex item shrink
  // below its content size — without this, long tokens (Solana base58
  // tickers, TON addresses) push the parent grid column wider than
  // its allocated 1fr and overflow the drawer. Combined with
  // ``overflow:hidden text-overflow:ellipsis`` the text truncates
  // gracefully; ``title`` carries the full string so a hover (or
  // long-press on mobile) reveals it.
  return (
    <div
      className="flex items-baseline"
      style={{ gap: 6, minWidth: 0 }}
    >
      <span
        style={{
          fontSize: 9,
          color: C.text4,
          flexShrink: 0,  // label keeps its width
        }}
      >
        {k}
      </span>
      <span
        className="font-mono-num"
        title={fullV ?? v}
        style={{
          fontSize: 10,
          color: C.text2,
          marginLeft: "auto",  // reproduces the old justify-between gap
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textAlign: "right",
        }}
      >
        {v}
      </span>
    </div>
  );
}

/** Middle-truncate a long string ``head…tail`` so addresses stay
 *  recognisable at a glance. Returns the input unchanged when it
 *  already fits. Used by ``SpreadLegBlock`` for ticker-as-address
 *  rendering — Solana base58 (44 chars) and TON (48 chars) tickers
 *  need this; CEX symbols (``BTCUSDT``, etc.) pass through. */
function abbrevAddr(s: string, headLen = 6, tailLen = 4): string {
  if (s.length <= headLen + tailLen + 1) return s;
  return `${s.slice(0, headLen)}…${s.slice(-tailLen)}`;
}

function Badge({ color, label, title }: { color: string; label: string; title?: string }) {
  return (
    <span
      title={title}
      style={{
        padding: "0 4px",
        fontSize: 9,
        fontWeight: 600,
        color,
        border: `1px solid ${color}30`,
        background: `${color}10`,
      }}
    >
      {label}
    </span>
  );
}

function Hint({
  children,
  tone = "muted",
}: {
  children: React.ReactNode;
  tone?: "muted" | "error";
}) {
  return (
    <div
      style={{
        padding: 16,
        textAlign: "center",
        fontSize: 11,
        color: tone === "error" ? C.red : C.text3,
      }}
    >
      {children}
    </div>
  );
}

function capitalize(s: string): string {
  return s.length > 0 ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function fmtLiq(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}

function timeAgoShort(iso: string | undefined): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "—";
  const sec = (Date.now() - t) / 1000;
  if (sec < 60) return `${Math.floor(sec)}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  return `${Math.floor(sec / 86400)}d`;
}

